/**
 * Error raised by fetchJSON in case of non-success response
 */
export class HTTPError extends Error {
	readonly response: Response
	readonly body: string | null

	constructor(response: Response, payload: string | null = null) {
		super(`${response.status} ${response.statusText}`)
		Object.setPrototypeOf(this, HTTPError.prototype);
		this.response = response
		this.body = payload
	}

	toString() {
		const base = super.toString()

		if (this.body) {
			return `${base} -- ${this.body}`
		}
	}
}

/**
 * Like fetch(), but throw in case of non-2xx error, unless `throw_` is set to `false`.
 */
export async function fetch_(
	// Simplify the typing to make the code easier to work with
	url: string,
	init?: RequestInit & {
		headers?: Record<string, string>,
		throw_?: boolean
	},
) {
	const response = await fetch(url, init)

	if (!response.ok && init && init.throw_ !== false) {
		let body: string | null = null

		try {
			body = await response.text()
		} catch (e) {
			console.error(`Got an error reading response text from ${url} with ${init}`)
		}

		throw new HTTPError(response, body)
	}

	return response
}

/**
 * Like fetch(), but parse JSON. In case of non-2xx response, log the error and return null.
 */
export async function fetchJSON<T = any>(
	// Simplify the typing to make the code easier to work with
	url: string,
	init?: RequestInit & {
		headers?: Record<string, string>,
		throw_?: boolean,
		log_error?: boolean,
		// Custom additions
		json?: any,
	}) {
	// Headers
	if (!init) {
		init = {}
	}

	if (!init.headers) {
		init.headers = {}
	}

	const set_headers = new Set(Object.keys(init.headers).map(header => header.toLowerCase()))

	if (!set_headers.has('content-type')) {
		init.headers['content-type'] = 'application/json'
	}
	if (!set_headers.has('accept')) {
		init.headers['accept'] = 'application/json'
	}

	// Body
	if (init.json !== undefined && init.body === undefined) {
		init.body = JSON.stringify(init.json)
		delete init.json
	}

	try {
		const response = await fetch_(url, init)
		return await response.json() as T
	} catch (e) {
		if (init?.log_error !== false) {
			console.error(`Got an error while fetching ${url} with ${JSON.stringify(init)}: ${e}`)
		}
		return null
	}
}
